import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";

export default function QuestionariosPage() {
  const { dark } = useTheme();

  // Totais
  const [stats, setStats] = useState({ total_formacao: 0, total_conteudo: 0, total_departamento: 0, total_usuario: 0 });
  const [loadingStats, setLoadingStats] = useState(true);

  // Ranking formações
  const [formacoes, setFormacoes] = useState([]);
  const [loadingFormacoes, setLoadingFormacoes] = useState(true);

  // Expansão
  const [expandedId, setExpandedId] = useState(null);
  const [detailType, setDetailType] = useState("usuario");
  const [rankingData, setRankingData] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(false);

  // Questionário (perguntas e respostas)
  const [questions, setQuestions] = useState([]);
  const [answersMap, setAnswersMap] = useState({});
  const [loadingQuestionario, setLoadingQuestionario] = useState(false);

  const [errorMsg, setErrorMsg] = useState("");

  // ══════════ Tokens de cor ══════════
  const t = dark
    ? {
        bg: "#080c14",
        bgCard: "#111827",
        bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)",
        border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)",
        textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0",
        textMuted: "#7b93b0",
        textActive: "#00b4ff",
        shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)",
        success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)",
        warning: "#f59e0b",
        warningBg: "rgba(245,158,11,0.12)",
        danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)",
      }
    : {
        bg: "#f4f7fc",
        bgCard: "#ffffff",
        bgHover: "#f0f6ff",
        bgActive: "#e6f0ff",
        border: "#e2e8f0",
        borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e",
        textSecondary: "#475569",
        textMuted: "#8b9eb0",
        textActive: "#0057ff",
        shadow: "0 8px 30px rgba(0,50,150,0.06)",
        rowHover: "#f8faff",
        success: "#16a34a",
        successBg: "#f0fdf4",
        warning: "#d97706",
        warningBg: "#fffbeb",
        danger: "#ef4444",
        dangerBg: "#fef2f2",
      };

  // ── Load dashboard stats ──────────────────────────
  const loadStats = useCallback(async () => {
    try {
      const res = await Api.getDashboard();
      const data = res?.data || res;
      if (data) {
        setStats({
          total_formacao: data.total_formacao || 0,
          total_conteudo: data.total_conteudo || 0,
          total_departamento: data.total_departamento || 0,
          total_usuario: data.total_usuario || 0,
        });
      }
    } catch {
      setErrorMsg("Erro ao carregar dados do dashboard.");
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // ── Load formação rankings ────────────────────────
  const loadFormacoes = useCallback(async () => {
    try {
      const res = await Api.rankingFormacao();
      const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setFormacoes(data);
    } catch {
      setErrorMsg("Erro ao carregar ranking de formações.");
    } finally {
      setLoadingFormacoes(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadFormacoes();
  }, [loadStats, loadFormacoes]);

  // ── Expandir / recolher ───────────────────────────
  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setRankingData([]);
      setQuestions([]);
      setAnswersMap({});
    } else {
      setExpandedId(id);
      setDetailType("usuario");
      setRankingData([]);
      setQuestions([]);
      setAnswersMap({});
    }
  };

  // ── Carregar ranking (usuário/departamento) ──────
  const loadRanking = useCallback(async (idFormacao, type) => {
    setLoadingRanking(true);
    try {
      let res;
      if (type === "usuario") {
        res = await Api.rankingUsuario({ id: idFormacao });
      } else {
        res = await Api.rankingDepartamento({ id: idFormacao });
      }
      const data = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setRankingData(data);
    } catch {
      setRankingData([]);
    } finally {
      setLoadingRanking(false);
    }
  }, []);

  // ── Carregar questionário (perguntas e respostas) ─
  const loadQuestionario = useCallback(async (idFormacao) => {
    setLoadingQuestionario(true);
    try {
      const resP = await Api.getPergunta({ id_formacao: idFormacao });
      const perguntasArr = Array.isArray(resP?.data) ? resP.data : Array.isArray(resP) ? resP : [];
      setQuestions(perguntasArr);

      // Carregar respostas em paralelo
      const map = {};
      const promises = perguntasArr.map(async (p) => {
        const resR = await Api.getResposta({ id_pergunta: p.id });
        const respostasArr = Array.isArray(resR?.data) ? resR.data : Array.isArray(resR) ? resR : [];
        map[p.id] = respostasArr;
      });
      await Promise.all(promises);
      setAnswersMap(map);
    } catch {
      setQuestions([]);
      setAnswersMap({});
    } finally {
      setLoadingQuestionario(false);
    }
  }, []);

  // Efeito quando expande ou muda tipo de detalhe
  useEffect(() => {
    if (expandedId) {
      loadRanking(expandedId, detailType);
      loadQuestionario(expandedId);
    }
  }, [expandedId, detailType, loadRanking, loadQuestionario]);

  const formatPercent = (valor) => {
    const num = parseFloat(valor);
    if (isNaN(num)) return "0%";
    return num.toFixed(0) + "%";
  };

  // ═══════════════ Estilos ═══════════════
  const styles = `
    .qt-page { font-family: 'Inter', system-ui, sans-serif; background: ${t.bg}; min-height: 100vh; padding: 32px 20px; }
    .qt-container { max-width: 1200px; margin: 0 auto; }
    .qt-header { margin-bottom: 32px; }
    .qt-heading { font-size: 1.65rem; font-weight: 700; letter-spacing: -0.03em; color: ${t.textPrimary}; }
    .qt-sub { font-size: 0.88rem; color: ${t.textMuted}; margin-top: 4px; }

    .qt-cards {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 16px;
      margin-bottom: 32px;
    }
    .qt-card {
      background: ${t.bgCard};
      border: 1px solid ${t.border};
      border-radius: 16px;
      padding: 18px 22px;
      transition: box-shadow 0.2s, border-color 0.2s;
    }
    .qt-card:hover { border-color: ${t.borderActive}; box-shadow: ${t.shadow}; }
    .qt-card-label { font-size: 0.74rem; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: ${t.textMuted}; margin-bottom: 8px; }
    .qt-card-value { font-size: 2rem; font-weight: 700; letter-spacing: -0.04em; color: ${t.textPrimary}; line-height: 1; }

    .qt-section-title { font-size: 1rem; font-weight: 700; color: ${t.textPrimary}; margin-bottom: 16px; }
    .qt-table-card { background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 18px; overflow: hidden; }
    .qt-table { width: 100%; border-collapse: collapse; }
    .qt-table thead th {
      text-align: left; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
      color: ${t.textMuted}; padding: 14px 22px; background: ${t.bgHover}; border-bottom: 1px solid ${t.border};
    }
    .qt-table tbody td { padding: 14px 22px; font-size: 0.88rem; color: ${t.textPrimary}; border-bottom: 1px solid ${t.border}; vertical-align: middle; }
    .qt-table tbody tr:last-child td { border-bottom: none; }
    .qt-table tbody tr:hover { background: ${t.rowHover}; }
    .qt-table tbody tr.expanded { background: ${t.bgHover}; }

    .qt-expand-icon {
      display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px;
      border-radius: 8px; cursor: pointer; transition: 0.2s; color: ${t.textSecondary};
    }
    .qt-expand-icon:hover { background: ${t.bgActive}; color: ${t.textActive}; }

    .qt-badge {
      display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 999px;
      font-size: 0.72rem; font-weight: 700;
    }
    .qt-badge.green { background: ${t.successBg}; color: ${t.success}; }
    .qt-badge.red { background: ${t.dangerBg}; color: ${t.danger}; }
    .qt-badge.yellow { background: ${t.warningBg}; color: ${t.warning}; }

    .qt-detail-row td { padding: 0; border-bottom: 1px solid ${t.border}; }
    .qt-detail-container { padding: 16px 22px 20px; border-top: 1px solid ${t.border}; }

    .qt-tabs { display: flex; gap: 0; margin-bottom: 16px; }
    .qt-tab {
      height: 36px; padding: 0 20px; border: 1px solid ${t.border}; background: transparent;
      color: ${t.textSecondary}; font-weight: 600; font-size: 0.82rem; cursor: pointer;
      transition: 0.2s; margin-right: -1px;
    }
    .qt-tab:first-child { border-radius: 10px 0 0 10px; }
    .qt-tab:last-child { border-radius: 0 10px 10px 0; }
    .qt-tab.active { background: ${t.bgActive}; color: ${t.textActive}; border-color: ${t.borderActive}; position: relative; z-index: 1; }
    .qt-tab:hover:not(.active) { background: ${t.bgHover}; color: ${t.textPrimary}; }

    .qt-question-title { font-size: 0.9rem; font-weight: 600; color: ${t.textPrimary}; margin-top: 20px; margin-bottom: 10px; }
    .qt-answer-list { display: flex; flex-direction: column; gap: 6px; padding-left: 16px; }
    .qt-answer-item { display: flex; align-items: center; gap: 8px; font-size: 0.86rem; color: ${t.textPrimary}; }
    .qt-answer-marker {
      width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
    }
    .qt-answer-marker.correct { background: ${t.success}; }
    .qt-answer-marker.incorrect { background: ${t.border}; }
    .qt-answer-text.correct { color: ${t.success}; font-weight: 600; }
    .qt-answer-text.incorrect { color: ${t.textSecondary}; }
  `;

  return (
    <div className="qt-page">
      <style>{styles}</style>
      <div className="qt-container">
        <div className="qt-header">
          <h1 className="qt-heading">Questionários</h1>
          <p className="qt-sub">Gerir questionários, perguntas e respostas</p>
        </div>

        {errorMsg && <div style={{ background: t.dangerBg, color: t.danger, padding: "14px 20px", borderRadius: 14, marginBottom: 24 }}>{errorMsg}</div>}
 

        {/* Formações com ranking + questionário */}
        <div className="qt-section-title">Formações</div>

        {loadingFormacoes ? (
          <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Carregando...</div>
        ) : formacoes.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: t.textMuted }}>Nenhuma formação encontrada.</div>
        ) : (
          <div className="qt-table-card">
            <table className="qt-table">
              <thead>
                <tr>
                  <th>Formação</th>
                  <th>Respostas</th>
                  <th>Certas</th>
                  <th>Erradas</th>
                  <th>% Acerto</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {formacoes.map((f) => (
                  <>
                    <tr key={f.id} className={expandedId === f.id ? "expanded" : ""}>
                      <td><span style={{ fontWeight: 600 }}>{f.formacao || "—"}</span></td>
                      <td>{f.total_respostas || 0}</td>
                      <td><span className="qt-badge green">{f.respostas_certas || 0}</span></td>
                      <td><span className="qt-badge red">{f.respostas_erradas || 0}</span></td>
                      <td>
                        <span className={`qt-badge ${parseFloat(f.percentual_acerto) >= 70 ? 'green' : parseFloat(f.percentual_acerto) >= 40 ? 'yellow' : 'red'}`}>
                          {formatPercent(f.percentual_acerto)}
                        </span>
                      </td>
                      <td>
                        <span className="qt-expand-icon" onClick={() => toggleExpand(f.id)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: expandedId === f.id ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </td>
                    </tr>
                    {expandedId === f.id && (
                      <tr className="qt-detail-row">
                        <td colSpan={6}>
                          <div className="qt-detail-container">
                            {/* Ranking */}
                            <div className="qt-tabs">
                              <button className={`qt-tab ${detailType === "usuario" ? "active" : ""}`} onClick={() => setDetailType("usuario")}>Por utilizador</button>
                              <button className={`qt-tab ${detailType === "departamento" ? "active" : ""}`} onClick={() => setDetailType("departamento")}>Por departamento</button>
                            </div>
                            {loadingRanking ? (
                              <div style={{ textAlign: "center", padding: 20, color: t.textMuted }}>Carregando ranking...</div>
                            ) : rankingData.length === 0 ? (
                              <div style={{ textAlign: "center", padding: 20, color: t.textMuted }}>Nenhum dado disponível.</div>
                            ) : (
                              <table className="qt-table" style={{ marginBottom: 20 }}>
                                <thead>
                                  <tr>
                                    <th>{detailType === "usuario" ? "Utilizador" : "Departamento"}</th>
                                    <th>Respostas</th>
                                    <th>Certas</th>
                                    <th>Erradas</th>
                                    <th>% Acerto</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {rankingData.map((item) => (
                                    <tr key={item.id}>
                                      <td><span style={{ fontWeight: 600 }}>{detailType === "usuario" ? item.nome : item.departamento}</span></td>
                                      <td>{item.total_respostas || 0}</td>
                                      <td><span className="qt-badge green">{item.respostas_certas || 0}</span></td>
                                      <td><span className="qt-badge red">{item.respostas_erradas || 0}</span></td>
                                      <td>
                                        <span className={`qt-badge ${parseFloat(item.percentual_acerto) >= 70 ? 'green' : parseFloat(item.percentual_acerto) >= 40 ? 'yellow' : 'red'}`}>
                                          {formatPercent(item.percentual_acerto)}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}

                            {/* Questionário */}
                            <div className="qt-section-title" style={{ marginTop: 8 }}>Perguntas e respostas</div>
                            {loadingQuestionario ? (
                              <div style={{ textAlign: "center", padding: 20, color: t.textMuted }}>Carregando questionário...</div>
                            ) : questions.length === 0 ? (
                              <div style={{ textAlign: "center", padding: 20, color: t.textMuted }}>Nenhuma pergunta cadastrada.</div>
                            ) : (
                              questions.map((q) => (
                                <div key={q.id}>
                                  <div className="qt-question-title">{q.pergunta}</div>
                                  <div className="qt-answer-list">
                                    {answersMap[q.id]?.map((resp) => (
                                      <div key={resp.id} className="qt-answer-item">
                                        <span className={`qt-answer-marker ${resp.correta === "true" ? "correct" : "incorrect"}`} />
                                        <span className={`qt-answer-text ${resp.correta === "true" ? "correct" : "incorrect"}`}>
                                          {resp.resposta} {resp.correta === "true" && "✓"}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}